import { tryParseAbpRemoteServiceError } from '@buxus/shared'
import { ABP_AUTH_TOKEN_STORAGE, ABP_CULTURE_COOKIE, ABP_TENANT_COOKIE } from './config'
import { getCookie } from './cookies'
import { notifyAuthSessionInvalid } from './authSessionFailure'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Thrown after `notifyAuthSessionInvalid` when an authenticated API call receives 401. */
export class UnauthorizedHttpError extends Error {
  override readonly name = 'UnauthorizedHttpError'
  constructor(message: string) {
    super(message)
  }
}

/** Non-OK ABP/API response with structured fields for analytics and UX mapping. */
export class AbpHttpError extends Error {
  override readonly name = 'AbpHttpError'
  readonly httpStatus: number
  readonly abpErrorCode: string | null
  readonly abpErrorDetails: string | null
  readonly responseBodyPreview: string | null

  constructor(params: {
    message: string
    httpStatus: number
    abpErrorCode?: string | null
    abpErrorDetails?: string | null
    responseBodyPreview?: string | null
  }) {
    super(params.message)
    this.httpStatus = params.httpStatus
    this.abpErrorCode = params.abpErrorCode ?? null
    this.abpErrorDetails = params.abpErrorDetails ?? null
    this.responseBodyPreview = params.responseBodyPreview ?? null
  }
}

export function isAbpHttpError(error: unknown): error is AbpHttpError {
  return error instanceof AbpHttpError
}

export type HttpOptions = {
  method?: HttpMethod
  body?: unknown
  auth?: boolean
  /** Bearer token to send instead of the token in local storage. */
  bearer?: string
  signal?: AbortSignal
  headers?: Record<string, string>
}

export async function httpJson<T>(url: string, options: HttpOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  }

  const culture = getCookie(ABP_CULTURE_COOKIE)
  if (culture) headers['.AspNetCore.Culture'] = culture

  const tenantId = getCookie(ABP_TENANT_COOKIE)
  if (tenantId) headers['Abp.TenantId'] = tenantId

  if (options.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`
  } else if (options.auth) {
    const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)
    if (token) headers.Authorization = `Bearer ${token}`
  }

  // ngrok free tunnels may serve an interstitial unless this header is present.
  // Match .ngrok.io and .ngrok-free.app (authSessionTeardown already uses includes('ngrok')).
  if (url.includes('ngrok')) {
    headers['ngrok-skip-browser-warning'] = 'true'
  }

  const res = await fetch(url, {
    method,
    headers,
    signal: options.signal,
    body: method === 'GET' ? undefined : JSON.stringify(options.body ?? {}),
    credentials: 'omit',
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const parsed = tryParseAbpRemoteServiceError(text)
    const message =
      parsed.message?.trim() ||
      parsed.details?.trim() ||
      `Request failed (${res.status})`
    if (options.auth && res.status === 401) {
      notifyAuthSessionInvalid()
      throw new UnauthorizedHttpError(
        message.trim() || 'Your session has expired. Please sign in again.',
      )
    }
    throw new AbpHttpError({
      message,
      httpStatus: res.status,
      abpErrorCode: parsed.code,
      abpErrorDetails: parsed.details,
      responseBodyPreview: text.trim() ? text.trim().slice(0, 500) : null,
    })
  }

  const text = await res.text()
  return (text ? JSON.parse(text) : {}) as T
}

export type HttpBlobResult = {
  blob: Blob
  /** From Content-Disposition when present. */
  fileName: string | null
}

/** Authenticated (or public) binary fetch — for PDF downloads and similar. */
export async function httpBlob(url: string, options: HttpOptions = {}): Promise<HttpBlobResult> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: '*/*',
    ...(options.headers ?? {}),
  }

  const culture = getCookie(ABP_CULTURE_COOKIE)
  if (culture) headers['.AspNetCore.Culture'] = culture

  const tenantId = getCookie(ABP_TENANT_COOKIE)
  if (tenantId) headers['Abp.TenantId'] = tenantId

  if (options.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`
  } else if (options.auth) {
    const token = localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)
    if (token) headers.Authorization = `Bearer ${token}`
  }

  if (url.includes('ngrok')) {
    headers['ngrok-skip-browser-warning'] = 'true'
  }

  const hasJsonBody = method !== 'GET' && options.body !== undefined
  if (hasJsonBody) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(url, {
    method,
    headers,
    signal: options.signal,
    body: hasJsonBody ? JSON.stringify(options.body) : undefined,
    credentials: 'omit',
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const parsed = tryParseAbpRemoteServiceError(text)
    const message =
      parsed.message?.trim() ||
      parsed.details?.trim() ||
      `Request failed (${res.status})`
    if (options.auth && res.status === 401) {
      notifyAuthSessionInvalid()
      throw new UnauthorizedHttpError(
        message.trim() || 'Your session has expired. Please sign in again.',
      )
    }
    throw new AbpHttpError({
      message,
      httpStatus: res.status,
      abpErrorCode: parsed.code,
      abpErrorDetails: parsed.details,
      responseBodyPreview: text.trim() ? text.trim().slice(0, 500) : null,
    })
  }

  const blob = await res.blob()
  return { blob, fileName: fileNameFromContentDisposition(res.headers.get('Content-Disposition')) }
}

function fileNameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  // filename*=UTF-8''...
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ''))
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header)
  if (plain?.[2]) return plain[2].trim()
  return null
}
